import { useCallback, useEffect, useState } from "react";
import { GitBranch, KeyRound, ShieldCheck, Store, Trash2, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/authStore";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import Modal from "../components/Modal";
import { relTime } from "../lib/format";

// Phase 6b: subtle sub-pill on the Shopify card showing collection publish
// health. Only renders when at least one collection is in a non-published
// state so the default Shopify card stays clean. Reads from
// /linkforge/merch/api/printify/publish-health which rides on the cl_access_token
// cookie set by the Phase 5b auth bridge.
function ShopifyPublishHealth() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/linkforge/merch/api/printify/publish-health", { credentials: "include" });
        if (!r.ok) throw new Error(String(r.status));
        const data = await r.json();
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (err) return null;
  const pending = rows.filter((r) => r.status === "published-pending").length;
  const failed = rows.filter((r) => r.status === "publish-failed").length;
  if (pending === 0 && failed === 0) return null;
  return (
    <div style={{ marginBottom: 10, display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="shopify-publish-health">
      {pending > 0 && (
        <span className="pill" style={{ background: "#fef3c7", color: "#854d0e", border: "1px solid #fde68a" }} data-testid="shopify-publish-pending">
          Collections: {pending} publishing
        </span>
      )}
      {failed > 0 && (
        <span className="pill" style={{ background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca" }} data-testid="shopify-publish-failed">
          Collections: {failed} publish-failed
        </span>
      )}
    </div>
  );
}

export default function IntegrationsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [statuses, setStatuses] = useState({}); // project_id -> status
  const [openConfig, setOpenConfig] = useState(null); // project_id
  const [pat, setPat] = useState("");
  const [saving, setSaving] = useState(false);
  const [reusing, setReusing] = useState(null);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null);

  const isDirector = user?.role === "director";

  const load = useCallback(async () => {
    const { data } = await api.get("/projects");
    setProjects(data);
    const map = {};
    for (const p of data) {
      try {
        const { data: s } = await api.get(`/projects/${p.id}/github/status`);
        map[p.id] = s;
      } catch {
        map[p.id] = null;
      }
    }
    setStatuses(map);
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeModal = () => {
    setOpenConfig(null);
    setPat("");
    setErr(null);
    setSuccess(null);
  };

  const savePat = async () => {
    if (!openConfig || !pat) return;
    setSaving(true); setErr(null); setSuccess(null);
    try {
      const { data } = await api.put(`/projects/${openConfig}/github/pat`, { pat });
      setSuccess(`Verified as ${data.verified_user} · scopes: ${(data.scopes || []).join(", ") || "(none reported)"} · last4 ${data.pat_last4}`);
      setPat("");
      await load();
      setTimeout(closeModal, 2500);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || "Failed to configure PAT."));
    } finally {
      setSaving(false);
    }
  };

  const reusePat = async (targetProject, sourceProject) => {
    if (!isDirector || !targetProject?.id || !sourceProject?.id) return;
    const ok = window.confirm(
      `Use the existing secured GitHub connection from ${sourceProject.name} for ${targetProject.name}? ` +
      "The credential will be verified against the target repository and will not be exposed to the browser."
    );
    if (!ok) return;
    setReusing(targetProject.id);
    try {
      await api.post(`/projects/${targetProject.id}/github/pat/reuse`, { source_project_id: sourceProject.id });
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      window.alert(typeof d === "string" ? d : (d?.message || "Could not reuse the existing GitHub connection."));
    } finally {
      setReusing(null);
    }
  };

  const clearPat = async (projectId) => {
    if (!isDirector) return;
    if (!window.confirm("Clear the GitHub PAT for this project? Read-only access will stop until a new PAT is configured.")) return;
    try {
      await api.delete(`/projects/${projectId}/github/pat`);
      await load();
    } catch (e) {
      console.error("clear pat failed", e);
    }
  };

  return (
    <>
      <TopBar title="Integrations" subtitle="External sources of truth wired into CommandLinc." />

      {/* Shopify Store — ForgeLinc Merch Studio sales channel. Phase 7a. */}
      <div className="panel" style={{ marginBottom: 14 }} data-testid="shopify-card">
        <div className="panel-head">
          <div>
            <h2><Store size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Shopify Store</h2>
            <p>Linked via ForgeLinc Merch Studio as the Printify sales channel.</p>
          </div>
          <span className="pill green" data-testid="shopify-status">Connected</span>
        </div>
        <div className="panel-body">
          {/* Phase 6b: non-scary caveat sub-pill — only shows when any
              collection row is in a non-published state. */}
          <ShopifyPublishHealth />
          <div className="wide-row">
            <div>
              <b>eewwjf-tt.myshopify.com</b>
              <p className="hint">
                channel: <span className="mono">Printify → Shopify (draft only)</span>
                {" · "}workspace: <span className="mono">ForgeLinc Merch Studio</span>
              </p>
              <small className="hint">
                Management lives inside the Merch Studio workspace (opens over LincWerks).
              </small>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
              <a
                href="/linkforge/merch/connect"
                className="btn small primary"
                data-testid="shopify-manage"
              >
                <Store size={12} /> Manage in Merch Studio
              </a>
              <a
                href="/linkforge/merch/collections"
                className="btn small"
                data-testid="shopify-open-collections"
              >
                Open collections
              </a>
            </div>
          </div>
        </div>
      </div>

      {projects.length === 0 ? (
        <EmptyState title="No projects yet." />
      ) : (
        projects.map((p) => {
          const s = statuses[p.id];
          const configured = s?.integration_status === "configured";
          const errored = s?.integration_status === "error";
          const reusableSource = projects.find((candidate) => (
            candidate.id !== p.id && statuses[candidate.id]?.integration_status === "configured"
          ));
          const toneClass = configured ? "green" : errored ? "red" : "amber";
          const label = configured ? "Configured" : errored ? "Auth error" : "Not configured";
          return (
            <div className="panel" key={p.id} style={{ marginBottom: 14 }} data-testid={`gh-card-${p.id}`}>
              <div className="panel-head">
                <div>
                  <h2><GitBranch size={14} style={{ verticalAlign: -2, marginRight: 6 }} />GitHub · {p.name}</h2>
                  <p>Read-only visibility: branches, pull requests, commits.</p>
                </div>
                <span className={`pill ${toneClass}`} data-testid={`gh-status-${p.id}`}>{label}</span>
              </div>
              <div className="panel-body">
                <div className="wide-row">
                  <div>
                    <b>{s?.repo_url || p.github?.repo_url || "(no repo url configured)"}</b>
                    <p className="hint">
                      default branch: <span className="mono">{s?.default_branch || "main"}</span>
                      {configured && s?.pat_last4 ? (
                        <> · PAT <span className="mono">••••{s.pat_last4}</span></>
                      ) : null}
                      {configured && s?.verified_user ? (
                        <> · verified as <span className="mono">{s.verified_user}</span></>
                      ) : null}
                      {configured && s?.verified_at ? (
                        <> · {relTime(s.verified_at)}</>
                      ) : null}
                    </p>
                    {configured && s?.scopes?.length ? (
                      <small className="hint">
                        scopes: {s.scopes.map((sc) => (<span key={sc} className="pill dim" style={{ marginRight: 4 }}>{sc}</span>))}
                      </small>
                    ) : null}
                    {!configured && reusableSource ? (
                      <small className="hint" style={{ display: "block", marginTop: 6 }}>
                        Existing secured connection available from <b>{reusableSource.name}</b>.
                      </small>
                    ) : null}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {configured ? (
                      <Link to={`/projects/${p.id}/github`} className="btn small" data-testid={`gh-open-${p.id}`}>
                        Open GitHub panel
                      </Link>
                    ) : null}
                    {isDirector ? (
                      <>
                        {!configured && reusableSource ? (
                          <button
                            className="btn small"
                            onClick={() => reusePat(p, reusableSource)}
                            disabled={reusing === p.id}
                            data-testid={`gh-reuse-${p.id}`}
                          >
                            <ShieldCheck size={12} /> {reusing === p.id ? "Verifying…" : `Use ${reusableSource.name} connection`}
                          </button>
                        ) : null}
                        <button
                          className="btn small primary"
                          onClick={() => { setOpenConfig(p.id); setErr(null); setSuccess(null); }}
                          data-testid={`gh-configure-${p.id}`}
                        >
                          <KeyRound size={12} /> {configured ? "Rotate PAT" : "Configure PAT"}
                        </button>
                        {configured ? (
                          <button
                            className="btn small danger"
                            onClick={() => clearPat(p.id)}
                            data-testid={`gh-clear-${p.id}`}
                          >
                            <Trash2 size={12} /> Clear
                          </button>
                        ) : null}
                      </>
                    ) : (
                      <small className="hint">Director required to configure</small>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}

      {openConfig ? (
        <Modal
          title="Configure GitHub PAT"
          eyebrow="INTEGRATION"
          onClose={closeModal}
          testId="gh-pat-modal"
        >
          <p className="hint" style={{ marginTop: 8 }}>
            Paste a GitHub Personal Access Token with <b>read</b> access to this repository.
            Fine-grained token needs <span className="mono">Contents: Read</span>,{" "}
            <span className="mono">Metadata: Read</span>, and{" "}
            <span className="mono">Pull Requests: Read</span>. Classic token needs the{" "}
            <span className="mono">repo</span> scope. The token is validated against GitHub,
            encrypted at rest, and never returned to any client.
          </p>
          <p className="hint">
            <a
              href="https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/managing-your-personal-access-tokens"
              target="_blank" rel="noreferrer"
            >GitHub PAT docs ↗</a>
          </p>
          <label style={{ display: "block", marginTop: 12 }}>
            <small className="hint">Personal Access Token</small>
            <input
              type="password"
              autoFocus
              value={pat}
              onChange={(e) => setPat(e.target.value)}
              placeholder="ghp_… or github_pat_…"
              data-testid="gh-pat-input"
              style={{ width: "100%", marginTop: 6 }}
            />
          </label>
          {err ? <p className="hint" style={{ color: "#ffb4be", marginTop: 10 }} data-testid="gh-pat-error"><XCircle size={12} style={{ verticalAlign: -2 }} /> {err}</p> : null}
          {success ? <p className="hint" style={{ color: "#7bf0be", marginTop: 10 }} data-testid="gh-pat-success"><ShieldCheck size={12} style={{ verticalAlign: -2 }} /> {success}</p> : null}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <button className="btn" onClick={closeModal}>Cancel</button>
            <button
              className="btn primary"
              disabled={!pat || saving}
              onClick={savePat}
              data-testid="gh-pat-save"
            >
              {saving ? "Verifying…" : "Save & verify"}
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
